<script setup>

import notification from "@/utils/plugins/NotificationService";
import { useAuthStore } from "@/app/stores/authStore";
import { useUiStore } from "@/app/stores/uiStore";
import ProjectsService from "@/api/services/projects.service";
import ClientService from "@/api/services/clients";
import TicketStatusService from "@/api/services/ticket.status.service";
import UsuarioExtranetService from "@/api/services/usersExtranet";
import EmployeeService from "@/api/services/employee.service";
import TicketPriorityService from "@/api/services/ticket.priority.service";
import TicketTypeService from "@/api/services/ticket.type.service";
import TicketService from "@/api/services/ticket.service";
import SharedTicketComments from "@/components/shared/SharedTicketComments.vue";
import TicketDocumentsField from "@/components/shared/TicketDocumentsField.vue";
import DepartmentService from "@/api/services/department.service";
import ClockifyButton from "@/components/shared/ClockifyButton.vue";
import { ElMessageBox } from "element-plus";
import { useRouter } from "vue-router";
import { computed, onMounted, ref, watch } from "vue";

import TicketStatusHistoryService from "@/api/services/ticket.status.history.service";
import { currentStatusOf, selectableStatuses } from "@/views/internal-tickets/ticketWorkflow";
import moment from "moment/moment";
import { filterByNormalizedText } from "@/utils/textSearch";

const authStore = useAuthStore();
const uiStore = useUiStore();
const router = useRouter();

const props = defineProps({
  action: {
    type: String,
    required: true
  },
  ticket: {
    type: Object,
    required: true
  }
})

const emits = defineEmits(['getTicket', 'saveData']);
// Si no se toca la fecha, el ticket se registra con la hora a la que se guarda, no a la que se abrió el formulario
const createdAtEdited = ref(false);
const errorList = ref({});
// eslint-disable-next-line no-unused-vars
const params = ref({});
const formModel = ref(props.ticket || {
  project: null,
  status: null,
  department: null,
});
const users = ref([]);
const clientUsers = ref([]);
const assignedEmployeeSearchQuery = ref('');
const requestingUserSearchQuery = ref('');
const affectedUserSearchQuery = ref('');
const types = ref([]);
const projects = ref([]);
const states = ref([]);
const priorities = ref([]);
const optionsAffectedUser = ref([]);
const optionsRegisterUser = ref([]);
const optionsDepartment = ref([]);
// eslint-disable-next-line no-unused-vars
const statusSelected = ref(null);
// Archivos elegidos al crear: se suben tras guardar el ticket (ver TicketDocumentsField)
const pendingFiles = ref([]);
const projectSelected = ref(null);
const clients = ref([]);
// eslint-disable-next-line no-unused-vars
const errors = ref(null);
// eslint-disable-next-line no-unused-vars
const user = computed(() => authStore.getUserLogged);
// Gestiona el ticket (Sistemas/admin, el asignado o un responsable de su departamento): borra adjuntos
// de otros y ve/publica comentarios privados. Misma regla que TicketAccessVoter en el back, que la revalida
const toIri = (value) => (value && typeof value === 'object' ? value['@id'] : value);
const canManageTicket = computed(() => {
  if (!user.value) {
    return false;
  }
  const roles = user.value.roles || [];
  if (roles.includes('ROLE_SYSTEMS') || roles.includes('ROLE_ADMIN')) {
    return true;
  }
  const assignedIri = toIri(formModel.value.assignedEmployee);
  if (assignedIri && assignedIri === user.value['@id']) {
    return true;
  }
  const departmentIri = toIri(formModel.value.department);
  return !!departmentIri && (user.value.managedDepartments || []).some(department => department['@id'] === departmentIri);
});
// eslint-disable-next-line no-unused-vars
const userAffected = computed(() => {
  return formModel.value &&
    formModel.value.affectedUser &&
    formModel.value.affectedUser.name + " " + formModel.value.affectedUser.lastName;
});
// Estados a los que el flujo permite pasar desde el actual (los valida el back)
const statesEnabled = computed(() => selectableStatuses(states.value, states.value.find(state => state['@id'] === formModel.value.status)));
// Último estado confirmado por el back: el selector vuelve a él si el cambio se cancela o se rechaza
let confirmedStatus = null;
const filteredAssignedEmployees = computed(() => {
  return filterByNormalizedText(users.value, assignedEmployeeSearchQuery.value, employee => employee.fullName);
});
const filteredRequestingUsers = computed(() => {
  return filterByNormalizedText(clientUsers.value, requestingUserSearchQuery.value, user => user.fullName);
});
const filteredAffectedUsers = computed(() => {
  return filterByNormalizedText(clientUsers.value, affectedUserSearchQuery.value, user => user.fullName || `${user.name} ${user.lastName}`);
});
// eslint-disable-next-line no-unused-vars
const userRegister = computed(() => {
  return formModel.value &&
    formModel.value.requestingUser &&
    formModel.value.requestingUser.name + " " + formModel.value.requestingUser.lastName;
});
// eslint-disable-next-line no-unused-vars
const isConnectUs = computed(() => {
  if (formModel.value.project) {
    if (typeof formModel.value.project === 'object' && formModel.value.project['id'] !== '/api/projects/316') {
      return false
    } else if (typeof formModel.value.project === 'string' && formModel.value.project !== '/api/projects/316') {
      return false
    }

    return true;

  } else {
    return false;
  }
});
watch(
  () => formModel.value.client,
  async (newValue) => {
    await uiStore.startLoading();
    let name = clients.value.find(client => client.id === newValue).name;
    await getProjects(name)
    await uiStore.stopLoading();
  }
);
watch(
  () => formModel.value.project,
  async (newValue) => {
    if (newValue && projects.value.length > 0) {
      await getClientUsers(formModel.value.client)
    }
  }
);
watch(
  () => formModel.value.requestingUser,
  (newValue) => {
    if (newValue) {
      let filteredUser = clientUsers.value.find(item => item['@id'] === newValue)
      if (filteredUser) {
        formModel.value.requestingUserEmail = filteredUser.email
        if (!formModel.value.affectedUser) {
          formModel.value.affectedUser = filteredUser['@id']
          formModel.value.affectedUserEmail = filteredUser.email
        }
      }

    }
  }
);
watch(
  () => formModel.value.affectedUser,
  (newValue) => {
    if (newValue) {
      let filteredUser = clientUsers.value.find(item => item['@id'] === newValue)
      if (filteredUser) {
        formModel.value.affectedUserEmail = filteredUser.email
      }

    }
  }
);


onMounted(async () => {
  await uiStore.startLoading();
  try {
    await Promise.all([
      getStates(),
      getPriorities(),
      getTypes(),
      getClients(),
      getUsers(), getDepartments()
    ]);

    if (props.action === "update") {


      formModel.value.status = currentStatusOf(props.ticket.ticketStatusHistories)?.['@id'] ?? null;
      confirmedStatus = formModel.value.status;
      if (typeof formModel.value.ticketPriority === "object") {
        formModel.value.ticketPriority = formModel.value.ticketPriority['@id'];
      }
      if (typeof formModel.value.ticketType === "object") {
        formModel.value.clockifyTagId = formModel.value.ticketType.clockifyTagId;
        formModel.value.ticketType = formModel.value.ticketType['@id'];

      }
      if (formModel.value.assignedEmployee) {
        formModel.value.assignedEmployee = typeof formModel.value.assignedEmployee === "object" ? formModel.value.assignedEmployee['@id'] : formModel.value.assignedEmployee;
      }
      if (props.ticket.affectedUser) {
        formModel.value.affectedUserEmail = typeof props.ticket.affectedUser === "object" ? props.ticket.affectedUser.email : null
        formModel.value.affectedUser = typeof props.ticket.affectedUser === "object" ? props.ticket.affectedUser['@id'] : props.ticket.affectedUser;
      }
      if (typeof formModel.value.requestingUser === "object") {
        formModel.value.requestingUserEmail = formModel.value.requestingUser.email
        formModel.value.requestingUser = formModel.value.requestingUser['@id'];

      }
      if (props.ticket.affectedEmployee) {
        formModel.value.affectedUserEmail = typeof props.ticket.affectedEmployee === "object" ? props.ticket.affectedEmployee.email : null
        formModel.value.affectedUser = typeof props.ticket.affectedEmployee === "object" ? props.ticket.affectedEmployee['@id'] : props.ticket.affectedEmployee;
      }
      if (props.ticket.employeeRequesting && typeof props.ticket.employeeRequesting === "object") {
        formModel.value.requestingUserEmail = props.ticket.employeeRequesting.email
        formModel.value.requestingUser = props.ticket.employeeRequesting['@id'];

      }
      if (typeof formModel.value.project === "object") {
        formModel.value.clockifyProjectId = formModel.value.project.clockifyProjectId;
        // formModel.value.project = formModel.value.project['@id'];
      }
      if (props.ticket.project) {
        formModel.value.client = props.ticket.project.client.id;
      }

    } else {
      let now = moment(new Date());
      formModel.value.project = projects.value[0];
      formModel.value.createdAt = now.format()
      formModel.value.status = states.value.find(v => v.name === "Abierto");
      formModel.value.ticketPriority = priorities.value.find(v => v.name === "Alta")['@id'];
    }
  } catch {
    uiStore.stopLoading();
  }
  projectSelected.value = typeof formModel.value.project === "object" ? formModel.value.project : projects.value.find(v => v["@id"] === formModel.value.project);

  if (projectSelected.value && projectSelected.value.client?.id) {
    await getClientUsers(projectSelected.value.client.id)
    await getProjects()
  }
  await uiStore.stopLoading();
})
const getClients = async () => {
  let params = {
    archived: false,
    itemsPerPage: 99999,
    page: 1,
  }
  const response = await ClientService.searchClients(params);
  clients.value = response.data["hydra:member"].filter(client => {
    return client.id === 98
    // return client.id === 12 || client.id === 98
    //  return client.id === 316 || client.id === 662
  });
}
const updateTicket = async () => {
  emits('getTicket')
}
// eslint-disable-next-line no-unused-vars

const getDepartments = async () => {
  try {
    const response = await DepartmentService.getDepartments({ acceptsTickets: true, itemsPerPage: 999 });
    optionsDepartment.value = response.data["hydra:member"];
    const current = props.ticket.department;
    if (props.action === "update" && current && typeof current === "object") {
      const iri = current["@id"] || (current.id ? "/api/departments/" + current.id : null);
      if (iri && !optionsDepartment.value.some(item => item["@id"] === iri)) optionsDepartment.value.push({ ...current, "@id": iri });
      formModel.value.department = iri;
    }
  } catch (e) {
    console.log(e)
  }

}
const getUsersExtranet = async () => {
  let clientId = null
  if (formModel.value && formModel.value.project && formModel.value.project.client) {
    clientId = formModel.value.project.client.id
  }

  const params = {
    id: clientId,
    itemsPerPage: 999999,
    page: 1
  }
  const response = await UsuarioExtranetService.getClientUsers(params, clientId);

  clientUsers.value = response.data["hydra:member"];

  return response.data["hydra:member"];
}
// eslint-disable-next-line no-unused-vars
const getRegisterUsers = async (obj) => {

  optionsRegisterUser.value = await getUsersExtranet(obj);
}
// eslint-disable-next-line no-unused-vars
const getUsersAffected = async (obj) => {
  optionsAffectedUser.value = await getUsersExtranet(obj);
}
const getProjects = async () => {
  let params = {
    archived: false,
    itemsPerPage: 99999,
    page: 1,
    'client.name': formModel.value.client ? formModel.value.client.name : 'sistemas etsa',
    'exists[client.extranetUsers]': true
  }
  await uiStore.startLoading();
  try {
    const res = await ProjectsService.getProjects(params);
    let data = projects.value = res.data["hydra:member"];
    if (formModel.value.client === 12) {
      projects.value = data.filter(project => project.id === 316)
    }
    if (formModel.value.client === 98) {
      projects.value = data.filter(project => project.id === 662)
    }
  } finally {
    uiStore.stopLoading();
  }
}
const getStates = async () => {
  const response = await TicketStatusService.getStates();
  states.value = response.data["hydra:member"];
  return response.data["hydra:member"];
}
const getTypes = async () => {
  const response = await TicketTypeService.getTypes();
  types.value = response.data["hydra:member"];
}
// eslint-disable-next-line no-unused-vars
const getClientUsers = async (id) => {
  const params = {
    itemsPerPage: 999999,
    page: 1
  }
  if (id == 12) {
    const response = await EmployeeService.getSystemEmployees()
    clientUsers.value = response.data["hydra:member"];
  } else {
    const response = await UsuarioExtranetService.getClientUsers(params, id);
    clientUsers.value = response.data["hydra:member"];
  }


}
// eslint-disable-next-line no-unused-vars
const getUsers = async () => {
  const response = await EmployeeService.getSystemEmployees();
  users.value = response.data["hydra:member"];
  return response.data["hydra:member"];
}
const filterAssignedEmployeesByName = (query) => {
  assignedEmployeeSearchQuery.value = query;
}
const filterRequestingUsersByName = (query) => {
  requestingUserSearchQuery.value = query;
}
const filterAffectedUsersByName = (query) => {
  affectedUserSearchQuery.value = query;
}
const getPriorities = async () => {
  const response = await TicketPriorityService.getPriorities();
  priorities.value = response.data["hydra:member"];
  return response.data["hydra:member"];
}
const changeState = () => {
  ElMessageBox.confirm('Vas a cambiar el estado del ticket, ¿Estás seguro?', '', {
    confirmButtonText: 'OK',
    cancelButtonText: 'Cancelar',
    type: 'warning',
  })
    .then(() => {
      TicketStatusHistoryService.createStatusHistory({ status: formModel.value.status, ticket: props.ticket['@id'] })
        .then((res) => {
          formModel.value.status = res.data.status;
          confirmedStatus = res.data.status;
          notification.addNotification({
            type: 'success',
            message: 'Estado cambiado correctamente',
            duration: 10000
          });
        })
        .catch((error) => {
          formModel.value.status = confirmedStatus;
          notification.addNotification({
            type: 'error',
            message: error?.response?.data?.detail ?? 'Error al cambiar el estado',
            duration: 10000
          });
        });
    })
    .catch(() => {
      formModel.value.status = confirmedStatus;
      notification.addNotification({
        type: 'info',
        message: 'Cambio de estado cancelado',
        duration: 10000
      });
    });
}

const assign = () => {
  if (!formModel.value.assignedEmployee) {
    notification.addNotification({
      type: 'error',
      message: 'Debe seleccionar un usuario',
      duration: 10000
    })
    //notification.addNotification("Debe seleccionar un usuario","error",10000)
    return
  }
  TicketService.updateTicket({ assignedEmployee: typeof formModel.value.assignedEmployee == 'object' ? formModel.value.assignedEmployee['@id'] : formModel.value.assignedEmployee }, props.ticket.id)
    .then(() => {
      notification.addNotification({
        type: 'success',
        message: 'Usuario asignado correctamente',
        duration: 10000
      })
      // notification.addNotification("Usuario asignado correctamente","success",10000)
    })
}
const submit = () => {
  errorList.value = {}
  let valid = true
  if (!formModel.value.project) {
    errorList.value.project = "El campo proyecto es requerido";
    valid = false
  }
  if (!formModel.value.ticketType) {
    errorList.value.ticketType = "El campo tipo es requerido";
    valid = false
  }
  if (!formModel.value.ticketPriority) {
    errorList.value.ticketPriority = "El campo prioridad es requerido";
    valid = false
  }
  if (!formModel.value.description) {
    errorList.value.description = "El campo descripción es requerido";
    valid = false
  }
  if (!formModel.value.affectedUserEmail) {
    errorList.value.affectedUserEmail = "El email del usuario afectado es requerido";
    valid = false
  }
  if (!formModel.value.affectedUser) {
    errorList.value.affectedUser = "El usuario afectado es requerido";
    valid = false
  }
  if (!formModel.value.requestingUser) {
    errorList.value.requestingUser = "El usuario solicitante es requerido";
    errorList.value.requestingUserEmail = "El email del usuario solicitante es requerido";
    valid = false
  }

  if (!formModel.value.phone) {
    errorList.value.phone = "El teléfono  es requeridos";
    valid = false
  }
  if (formModel.value.phone.length !== 9) {
    errorList.value.phone = "El teléfono debe tener 9 dígitos";
    valid = false
  }
  if (!formModel.value.issue) {
    errorList.value.issue = "El asunto es requerido";
    valid = false
  }


  if (!valid) {
    notification.addNotification({
      type: 'error',
      message: 'No se pudo completar el proceso, ha de rellenar todos los campos marcados y requeridos'
    })
  } else {
    formModel.value.internal = false
    if (props.action === 'create' && !createdAtEdited.value) {
      formModel.value.createdAt = moment().format();
    }
    emits('saveData', formModel.value, pendingFiles.value)
  }

}



</script>

<template>
  <div class="container-fluid ticket-form">
    <div class="w-100">
      <el-row :gutter="20" class="ticket-form__header">
        <el-col :xs="24" :md="6" class="d-flex align-items-center ticket-form__title-col">
          <img src="@/assets/icons/group.svg" alt="" class="mt-3">
          <div class="title-text ml-4 mt-3">
            Tickets
          </div>
        </el-col>
        <el-col v-if="formModel" :xs="24" :md="18">
          <div>
            <el-row :gutter="20" class="ticket-form__row">
              <el-col :xs="24" :md="6" v-if="action === 'create'">
                <div class="form-item">
                  <label for="project mr-2">Cliente</label>
                  <el-select class="w-100" placeholder="Cliente"
                    :disabled="action !== 'create' && ((ticket.employeeCreatingTicket && authStore.getUserLogged.id !== ticket.employeeCreatingTicket.id) || !ticket.employeeCreatingTicket)"
                    filterable v-model="formModel.client">
                    <el-option v-for="item in clients" :key="item.id" :label="item.name" :value="item.id" />
                  </el-select>
                </div>
              </el-col>
              <el-col :xs="24" :md="6" v-if="formModel.client">
                <div class="form-item">
                  <label for="project mr-2">Proyecto</label>
                  <el-select class="w-100" placeholder="Selecciona primero un Proyecto"
                    :disabled="action !== 'create' && ((ticket.employeeCreatingTicket && authStore.getUserLogged.id !== ticket.employeeCreatingTicket.id) || !ticket.employeeCreatingTicket)"
                    filterable v-model="formModel.project">
                    <el-option v-for="item in projects" :key="item.id" :label="item.name" :value="item['@id']" />
                  </el-select>
                </div>
                <p v-if="errorList && errorList.project" class="animated fadeIn invalid-feedback">
                  {{ errorList.project }}
                </p>
              </el-col>
              <el-col v-if="action !== 'create'" :xs="24" :md="12">
                <div class="form-item">
                  <label for="code">Código</label>
                  <el-input disabled :value="'[' + formModel.tag + '] - ' + formModel.id" />
                </div>
              </el-col>
            </el-row>
          </div>
        </el-col>
      </el-row>
    </div>
    <div v-if="formModel && formModel.project && !uiStore.loading" class="card mb-5 wback ticket-form__card"
      style="background-image: '@/assets/images/ticket-back.png';">
      <div class="card-body">
        <el-row :gutter="20" class="ticket-form__row">
          <el-col v-if="action !== 'create' && formModel" :xs="24" :md="8">
            <div class="form-item">
              <label for="state">Estado</label>
              <el-select class="w-100" @change="changeState" v-model="formModel.status" filterable
                :disabled="!authStore.getUserLogged.roles.includes('ROLE_SYSTEMS')">
                <el-option v-for="item in statesEnabled" :key="item.id" :label="item.name" :value="item['@id']" />
              </el-select>
            </div>
          </el-col>
          <el-col :xs="24" :md="16">
            <div class="form-item">
              <label for="issue">Asunto</label>
              <el-input v-model="formModel.issue" />
            </div>
            <p v-if="errorList && errorList.issue" class="animated fadeIn invalid-feedback">
              {{ errorList.issue }}
            </p>
          </el-col>
          <el-col :xs="24" :md="8">
            <div class="form-item">
              <label for="typeTicket" class="w-100">Tipo</label>
              <el-select class="w-100" filterable v-model="formModel.ticketType" size="large">
                <el-option v-for="item in types" :key="item.id" :label="item.name" :value="item['@id']" />
              </el-select>
            </div>
            <p v-if="errorList && errorList.ticketType" class="animated fadeIn invalid-feedback">
              {{ errorList.ticketType }}
            </p>
          </el-col>
          <el-col :xs="24" :md="8">
            <div class="form-item">
              <label for="user" class="w-100">Usuario asignado</label>
              <el-select class="w-100" filterable :filter-method="filterAssignedEmployeesByName"
                v-model="formModel.assignedEmployee">
                <el-option v-for="item in filteredAssignedEmployees" :key="item.id" :label="item.fullName" :value="item['@id']" />
              </el-select>
            </div>
          </el-col>
          <el-col v-if="props.action === 'update'" :xs="24" :md="8">
            <div class="ticket-form__assign-action">
              <button class="btn btn_yellow btn-assign" @click="assign()">
                ASIGNAR
              </button>
            </div>
          </el-col>
        </el-row>
      </div>
    </div>



    <el-row v-if="formModel && formModel.project" :gutter="10" class="ticket-form__row">
      <el-col :xs="24" :lg="8">
        <div class="form-item">
          <label for="user" class="w-100">Solicitante</label>
          <el-select class="w-100" filterable :filter-method="filterRequestingUsersByName"
            v-model="formModel.requestingUser">
            <el-option v-for="item in filteredRequestingUsers" :key="item.id" :label="item.fullName" :value="item['@id']" />
          </el-select>
        </div>
        <p v-if="errorList && errorList.requestingUser" class="animated fadeIn invalid-feedback">
          {{ errorList.requestingUser }}
        </p>
      </el-col>
      <el-col :xs="24" :lg="8">
        <div class="form-item">
          <label for="phone">Teléfono/Extensión</label>
          <el-input v-model="formModel.phone" type="text" />
          <p v-if="errorList && errorList.phone" class="animated fadeIn invalid-feedback">
            {{ errorList.phone }}
          </p>
        </div>
      </el-col>
      <el-col :xs="24" :lg="8">
        <el-row :gutter="10" class="ticket-form__row">
          <el-col :xs="24" :sm="12">
            <div class="form-item date-picker">
              <label for="createdAt">Fecha creación</label>
              <el-date-picker class="w-100" v-model="formModel.createdAt" :disabled="action !== 'create' && !canManageTicket" type="datetime" format="DD/MM/YYYY HH:mm:ss"
                value-format="YYYY-MM-DDTHH:mm:ssZ" :picker-options="{ firstDayOfWeek: 1 }" @change="createdAtEdited = true" />
            </div>
          </el-col>
          <el-col :xs="24" :sm="12">
            <div class="form-item date-picker">
              <label for="createdAt">Fecha cierre</label>
              <el-date-picker class="w-100" v-model="formModel.closingDate" disabled name="from-date" type="date"
                format="DD/MM/YYYY" value-format="YYYY-MM-DD" :picker-options="{ firstDayOfWeek: 1 }" />
            </div>
          </el-col>
        </el-row>
      </el-col>
      <el-col :xs="24" :md="8">
        <div class="form-item">
          <label for="userAffected" class="w-100">Usuario afectado</label>
          <el-select class="w-100" filterable :filter-method="filterAffectedUsersByName"
            v-model="formModel.affectedUser">
            <el-option v-for="item in filteredAffectedUsers" :key="item.id" :label="item.name + ' ' + item.lastName"
              :value="item['@id']" />
          </el-select>
        </div>
        <p v-if="errorList && errorList.affectedUser" class="animated fadeIn invalid-feedback">
          {{ errorList.affectedUser }}
        </p>
      </el-col>
      <el-col :xs="24" :md="8">
        <div class="form-item">
          <label for="department" class="w-100">Departamento</label>
          <el-select class="w-100" filterable v-model="formModel.department">
            <el-option v-for="item in optionsDepartment" :key="item.id" :label="item.name" :value="item['@id']" />
          </el-select>
        </div>
      </el-col>
      <el-col :xs="24" :md="8">
        <div class="form-item">
          <label for="equipmentTag">Etiqueta del equipo</label>
          <el-input type="text" v-model="formModel.equipmentTag" />
        </div>
      </el-col>
      <el-col :xs="24" :md="8">
        <div class="form-item">
          <label for="affectedUserEmail">Email usuario afectado</label>
          <el-input disabled type="text" v-model="formModel.affectedUserEmail" />
        </div>
        <p v-if="errorList && errorList.affectedUser && errorList.affectedUserEmail"
          class="animated fadeIn invalid-feedback">
          {{ errorList.affectedUserEmail }}
        </p>
      </el-col>
      <el-col :xs="24" :md="8">
        <div class="form-item">
          <label for="requestingUserEmail">Email Solicitante</label>
          <el-input disabled type="text" v-model="formModel.requestingUserEmail" />
        </div>
        <p v-if="errorList && errorList.requestingUser && errorList.requestingUserEmail"
          class="animated fadeIn invalid-feedback">
          {{ errorList.requestingUserEmail }}
        </p>
      </el-col>
      <el-col :xs="24" :md="8">
        <ClockifyButton v-if="action !== 'create'" link="" :ticket="ticket"
          :clockifyProjectId="formModel.clockifyProjectId" :clockify-tag-id="formModel.clockifyTagId"
          @update-ticket="updateTicket" />
      </el-col>
    </el-row>
    <el-row v-if="formModel && formModel.project">
      <el-col :span="24">
        <div class="form-item">
          <label for="description">Descripción</label>
          <el-input v-model="formModel.description" :autosize="{ minRows: 8 }" type="textarea" />
        </div>
        <p v-if="errorList && errorList.description" class="animated fadeIn invalid-feedback">
          {{ errorList.description }}
        </p>
      </el-col>
    </el-row>
    <el-row v-if="formModel && formModel.project" :gutter="20" class="ticket-form__row">
      <el-col :xs="24" :md="12">
        <TicketDocumentsField :ticket-id="action === 'create' ? null : ticket.id" :documents="ticket.ticketDocuments || []"
          v-model:pending-files="pendingFiles" :can-manage="canManageTicket" />
      </el-col>
      <el-col :xs="24" :md="12">
        <el-row :gutter="20" class="ticket-form__row">
          <el-col :xs="24" :span="24">
            <div class="form-item">
              <label for="priority" class="w-100">Prioridad</label>
              <el-select class="w-100" size="large" filterable v-model="formModel.ticketPriority">
                <el-option v-for="item in priorities" :key="item.id" :label="item.name" :value="item['@id']" />
              </el-select>
              <p v-if="errorList && errorList.ticketPriority" class="animated fadeIn invalid-feedback">
                {{ errorList.ticketPriority }}
              </p>
            </div>
          </el-col>
          <div class="pl-2 ticket-form__flags">
            <el-col :xs="24" :span="24">
              <input v-show="formModel.isByPhone" id="isByPhone" type="radio" class="align-self-center radio-larg"
                name="isByPhone" checked @click.prevent="formModel.isByPhone = !formModel.isByPhone">
              <input v-show="!formModel.isByPhone" id="isByPhone" type="radio" class="align-self-center radio-larg"
                name="isByPhone" @click.prevent="formModel.isByPhone = !formModel.isByPhone">
              <label for="isByPhone" class="ml-2"> Llamada telefónica</label>
            </el-col>
            <el-col :xs="24" :span="24">
              <input v-show="formModel.isOnSite" id="isOnSite" type="radio" class="align-self-center radio-larg"
                name="isOnSite" checked @click.prevent="formModel.isOnSite = !formModel.isOnSite">
              <input v-show="!formModel.isOnSite" id="isOnSite" type="radio" class="align-self-center radio-larg"
                name="isOnSite" @click.prevent="formModel.isOnSite = !formModel.isOnSite">
              <label for="isOnSite" class="ml-2">Resuelto in situ</label>
            </el-col>
            <el-col :xs="24" :span="24">
              <input v-show="formModel.isProjectEntity" id="isProjectEntity" type="radio"
                class="align-self-center radio-larg" name="isProjectEntity" checked
                @click.prevent="formModel.isProjectEntity = !formModel.isProjectEntity">
              <input v-show="!formModel.isProjectEntity" id="isProjectEntity" type="radio"
                class="align-self-center radio-larg" name="isProjectEntity"
                @click.prevent="formModel.isProjectEntity = !formModel.isProjectEntity">
              <label for="isProjectEntity" class="ml-2"> Entidad proyecto</label>
            </el-col>
          </div>
        </el-row>
      </el-col>
    </el-row>
    <el-row v-if="formModel && action !== 'create' && formModel.project" :gutter="20" class="ticket-form__row">
      <!-- Las notas privadas solo las ve y publica quien gestiona el ticket (el back filtra igual) -->
      <el-col :xs="24" :md="12" v-if="canManageTicket">
        <SharedTicketComments title="Comentarios privados" :ticket="ticket" :is-private="true"
          placeholder="Escriba un comentario" />
      </el-col>
      <el-col :xs="24" :md="12">
        <SharedTicketComments title="Comentarios públicos" :ticket="ticket" :is-private="false"
          placeholder="Escriba un comentario" />
      </el-col>
    </el-row>
    <el-row v-if="formModel.project">
      <div class="d-flex justify-content-end align-items-center mt-lg-5 ticket-form__actions">
        <button class="btn btn_yellow search mr-5" @click="router.go(-1)">
          VOLVER
        </button>
        <button class="btn btn_darkpink search" @click.prevent="submit">
          GUARDAR
        </button>
      </div>
    </el-row>
  </div>
</template>

<style scoped lang="scss">
.ticket-form {
  padding-bottom: 24px;
}

.ticket-form__card {
  border-radius: 18px;
}

.ticket-form__row {
  row-gap: 8px;
}

.ticket-form__assign-action {
  margin-top: 2.8em;
}

.ticket-form__file-box {
  word-break: break-word;
}

.ticket-form__flags {
  width: 100%;
}

.ticket-form__actions {
  width: 100%;
}

.btn-light {
  height: 42px !important;
}

.btn-assign {
  height: 42px;
  color: #474CE6;
  min-width: 200px;
  font-weight: 400;
  font-size: 20px;
  width: 300px;
  font-family: "Montserrat";
}

.search {
  height: 42px;
  width: 300px;
}

.wback {
  background-image: url('../../assets/images/ticket-back.png');
  background-repeat: no-repeat;
  background-size: contain;
}

label {
  display: inline-block;
  margin-bottom: 0.5rem;
  margin-top: 8px;
  font-family: Montserrat;
  font-weight: 800;
  line-height: 24px;
  letter-spacing: -0.05em;
  text-align: left;
}

label.checkbox-label input[type=checkbox] {
  position: relative;
  vertical-align: middle;
  bottom: 1px;
}

@media (max-width: 767px) {
  .ticket-form {
    padding-left: 8px;
    padding-right: 8px;
  }

  .ticket-form__row {
    row-gap: 0px;
  }

  .ticket-form__header {
    margin-bottom: 10px;
  }

  .ticket-form__title-col {
    margin-bottom: 8px;
  }

  .title-text {
    font-size: 2.2em;
  }

  .ticket-form__card {
    margin-left: 0;
    margin-right: 0;
    background-size: 92px;
    background-position: calc(100% + 16px) 12px;
  }

  .ticket-form__assign-action {
    margin-top: 0.5rem;
  }

  .ticket-form__flags {
    padding-left: 10px;
    padding-right: 10px;
  }

  .ticket-form__file-box {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }

  .ticket-form__actions {
    flex-direction: column-reverse;
    align-items: stretch !important;
    gap: 10px;
    margin-top: 18px;
    padding-left: 4px;
    padding-right: 4px;
  }

  .btn-assign,
  .search {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
    font-size: 18px;
  }

  .ticket-form__actions .mr-5 {
    margin-right: 0 !important;
  }

  :deep(.el-select),
  :deep(.el-date-editor.el-input),
  :deep(.el-date-editor.el-input__wrapper),
  :deep(.el-date-editor.el-range-editor),
  :deep(.el-input),
  :deep(.el-textarea) {
    width: 100%;
  }
}
</style>
